import { format as formatDate, parseISO } from 'date-fns';
import deLocale from 'date-fns/locale/de';
import enLocale from 'date-fns/locale/en-US';
const dateLocales = {
    de: deLocale,
    en: enLocale,
};
/**
 * Returns the source for an Image object
 *
 * @param image Image object
 * @param size Resize and cropping parameters (eg. "1200x800_crop", defaults to original size)
 * @param name Name of the file (overrides the original filename)
 */
export function image(image, size = '0x0_ratio', extension = null, name = null) {
    if (!image) {
        return '';
    }
    if (typeof image === 'string') {
        return encodeURI(`/img/${image}/${size}/${image}`);
    }
    if (image.extension.toLowerCase() === 'svg') {
        return `/media/${image.hashId}`;
    }
    return encodeURI(`/img/${image.hashId}/${size}/${name || image.name}.${extension || image.extension}?${image.timestamp}`);
}
/**
 * Finds objects with a certain tag value
 *
 * @param items Array of taggable objects to search trough
 * @param tagValue The tag value to find
 */
export function tagged(items, tagValue) {
    return items.filter((item) => {
        return item.tags.find(tag => tag.value == tagValue) || false;
    });
}
/**
 * Finds the first object with a certain tag value
 *
 * @param items Array of taggable objects to search trough
 * @param tagValue The tag value to find
 */
export function taggedFirst(items, tagValue) {
    return tagged(items, tagValue)[0] || null;
}
/**
 * Returns a localized string for a given translation key
 *
 * @param key Translation key
 */
export function trans(key) {
    return window.TRANSLATIONS[key] || key;
}
/**
 * Strips all HTML tags from a string
 *
 * @param string String of HTML content
 */
export function stripTags(string) {
    const html = string;
    let div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
}
/**
 * Scrolls smoothly to a given element
 *
 * @param target Element to scroll to
 * @param duration Duration of animation
 */
export function scrollTo(target) {
    window.scrollTo({
        top: target.offsetTop,
        behavior: "smooth"
    });
}
/**
 * Returns the currently set locale
 */
export function locale() {
    return document.documentElement.getAttribute('lang') || 'de';
}
/**
 * Emits a modal open event to the Vue root component
 *
 * @param caller Vue root element to notify
 * @param component Modal component
 */
export function modal(caller, component) {
    caller.openModal(component);
}
/**
 * Local storage helper functions
 */
export const storage = {
    set(key, value) {
        try {
            localStorage.setItem('NITROUS_' + key, JSON.stringify(value));
        }
        catch (ex) {
            console.log('Could not write to locale storage', {
                message: ex.message
            });
        }
    },
    get(key) {
        let value = localStorage.getItem('NITROUS_' + key) || null;
        return value ? JSON.parse(value) : null;
    },
    remove(key) {
        localStorage.removeItem('NITROUS_' + key);
    }
};
/**
 * Local storage helper functions
 */
export const session = {
    set(key, value) {
        try {
            sessionStorage.setItem('NITROUS_' + key, JSON.stringify(value));
        }
        catch (ex) {
            console.log('Could not write to locale storage', {
                message: ex.message
            });
        }
    },
    get(key) {
        let value = sessionStorage.getItem('NITROUS_' + key) || null;
        return value ? JSON.parse(value) : null;
    },
    remove(key) {
        sessionStorage.removeItem('NITROUS_' + key);
    }
};
/**
 * Returns a formatted and localized date string
 *
 * @param date Date string to format
 * @param format Format tokens
 */
export function localizedDate(date, format = 'do MMMM yyyy') {
    return formatDate(parseISO(date), format, {
        locale: dateLocales[locale() || 'de']
    });
}
/**
 * Returns a formatted and localized datetime string
 *
 * @param datetime Datetime string to format
 * @param format Format tokens
 */
export function localizedDateTime(datetime, format = 'do MMMM yyyy, HH:mm') {
    return formatDate(parseISO(datetime), format, {
        locale: dateLocales[locale() || 'de']
    });
}
const debounceStack = [];
export function debounce(delay, key = 'global') {
    return new Promise(resolve => {
        clearTimeout(debounceStack[key]);
        debounceStack[key] = setTimeout(resolve, delay);
    });
}
export function isExternalLink(url) {
    const domain = window.location.protocol + "//" + window.location.hostname;
    if (url.indexOf("/media/") !== -1) {
        // Media download link
        return true;
    }
    else if (url.indexOf(domain) !== -1) {
        // Internal absolute
        return false;
    }
    else if (url.indexOf("http") === -1) {
        // Internal relative
        return false;
    }
    else {
        // External
        return true;
    }
}
/**
 * Returns a slugified version of a string
 *
 * @param text Input text
 */
export function slugify(text) {
    text = text.replace(/^\s+|\s+$/g, '');
    // Make the string lowercase
    text = text.toLowerCase();
    // Remove accents, swap ñ for n, etc
    var from = "ÁÄÂÀÃÅČÇĆĎÉĚËÈÊẼĔȆÍÌÎÏŇÑÓÖÒÔÕØŘŔŠŤÚŮÜÙÛÝŸŽáäâàãåčçćďéěëèêẽĕȇíìîïňñóöòôõøðřŕšťúůüùûýÿžþÞĐđßÆa·/_,:;";
    var to = "AAAAAACCCDEEEEEEEEIIIINNOOOOOORRSTUUUUUYYZaaaaaacccdeeeeeeeeiiiinnooooooorrstuuuuuyyzbBDdBAa------";
    for (var i = 0, l = from.length; i < l; i++) {
        text = text.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i));
    }
    // Remove invalid chars
    text = text.replace(/[^a-z0-9 -]/g, '')
        // Collapse whitespace and replace by -
        .replace(/\s+/g, '-')
        // Collapse dashes
        .replace(/-+/g, '-');
    return text;
}
/**
 * Gets a CSS variable
 *
 * @param name Variable name
 */
export function cssVar(name) {
    return getComputedStyle(document.documentElement).getPropertyValue(name) || null;
}
/**
 * Returns true if the current viewport width
 * matches the given breakpoint (upwards)
 *
 * @param breakpoint Name of breakpoint, for example 'md'
 */
export function matchesBreakpointUp(breakpoint) {
    const minWidth = cssVar('--breakpoint-' + breakpoint);
    return window.matchMedia(`(min-width: ${minWidth})`).matches;
}
/**
 * Returns true if the current viewport width
 * matches the given breakpoint (downwards)
 *
 * @param breakpoint Name of breakpoint, for example 'md'
 */
export function matchesBreakpointDown(breakpoint) {
    const maxWidth = cssVar('--breakpoint-' + breakpoint);
    return window.matchMedia(`(max-width: ${maxWidth})`).matches;
}
/**
 * Format a currency amount
 * given a currency and locale
 */
export function formatCurrency(amount, currency = 'CHF', locales = null) {
    return new Intl.NumberFormat(locales || locale(), {
        style: 'currency',
        currency
    }).format(amount);
}
export default {
    image,
    locale,
    localizedDate,
    localizedDateTime,
    trans,
    tagged,
    taggedFirst,
    stripTags,
    slugify,
    scrollTo,
    modal,
    formatCurrency,
    cssVar,
    isExternalLink,
    matchesBreakpointUp,
    matchesBreakpointDown,
    storage,
    session,
    debounce,
};
